import React, { useState, useEffect, useMemo } from 'react';
import { testPrepAPI } from './testPrepApi';

const ACCENT = '#1e3a5f';
const ACCENT_LIGHT = '#2a5080';
const SUCCESS_GREEN = '#27ae60';
const WARNING_AMBER = '#f39c12';
const ERROR_RED = '#c0392b';
const BORDER_GRAY = '#dce1e8';
const BG_LIGHT = '#f4f6f9';
const TEXT_DARK = '#1a1a2e';
const TEXT_MID = '#5a6170';
const WHITE = '#ffffff';
const CARD_SHADOW = '0 2px 12px rgba(0,0,0,0.06)';

const styles = {
  container: {
    minHeight: '100vh',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: TEXT_DARK,
    background: BG_LIGHT,
  },
  header: {
    background: ACCENT,
    color: WHITE,
    padding: '32px 40px',
    textAlign: 'center',
  },
  headerTitle: { fontSize: 14, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1.5, opacity: 0.8, marginBottom: 8 },

  hero: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 48,
    padding: '40px 40px 32px',
    background: WHITE,
    borderBottom: `1px solid ${BORDER_GRAY}`,
  },
  scoreCircle: {
    width: 160,
    height: 160,
    borderRadius: '50%',
    border: `6px solid ${ACCENT}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  scoreValue: { fontSize: 48, fontWeight: 800, color: ACCENT, lineHeight: 1 },
  scoreLabel: { fontSize: 13, color: TEXT_MID, marginTop: 4 },
  statBox: { textAlign: 'center' },
  statValue: { fontSize: 28, fontWeight: 700, color: TEXT_DARK },
  statLabel: { fontSize: 13, color: TEXT_MID, marginTop: 4 },

  content: { maxWidth: 1000, margin: '0 auto', padding: '32px 24px' },
  card: {
    background: WHITE,
    borderRadius: 10,
    border: `1px solid ${BORDER_GRAY}`,
    boxShadow: CARD_SHADOW,
    padding: '24px 28px',
    marginBottom: 24,
  },
  cardTitle: { fontSize: 16, fontWeight: 700, marginBottom: 20, color: ACCENT },

  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    textAlign: 'left',
    padding: '10px 14px',
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    color: TEXT_MID,
    borderBottom: `2px solid ${BORDER_GRAY}`,
  },
  td: {
    padding: '12px 14px',
    fontSize: 14,
    borderBottom: `1px solid ${BG_LIGHT}`,
  },

  barContainer: { display: 'flex', flexDirection: 'column', gap: 14 },
  barRow: { display: 'flex', alignItems: 'center', gap: 12 },
  barLabel: { width: 160, fontSize: 13, fontWeight: 500, color: TEXT_DARK, flexShrink: 0 },
  barTrack: { flex: 1, height: 22, borderRadius: 4, background: BG_LIGHT, overflow: 'hidden', position: 'relative' },
  barFill: { height: '100%', borderRadius: 4, transition: 'width 0.5s ease' },
  barValue: { width: 48, fontSize: 13, fontWeight: 600, textAlign: 'right', color: TEXT_MID },

  twoCol: { display: 'flex', gap: 24, flexWrap: 'wrap' },
  col: { flex: '1 1 45%', minWidth: 280 },

  listItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    marginBottom: 12,
    fontSize: 14,
    lineHeight: 1.6,
  },
  bullet: { fontSize: 16, lineHeight: 1.4, flexShrink: 0 },

  gradeBox: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 56,
    height: 56,
    borderRadius: 8,
    fontSize: 28,
    fontWeight: 800,
    marginRight: 16,
  },
  gradeExplanation: { fontSize: 14, color: TEXT_MID, lineHeight: 1.6 },

  actions: {
    display: 'flex',
    gap: 14,
    justifyContent: 'center',
    padding: '32px 0 48px',
    flexWrap: 'wrap',
  },
  actionBtn: {
    padding: '14px 28px',
    borderRadius: 8,
    border: 'none',
    cursor: 'pointer',
    fontSize: 15,
    fontWeight: 600,
    transition: 'transform 0.1s',
  },
};

function gradeColor(grade) {
  if (grade === 'A' || grade === 'A+') return SUCCESS_GREEN;
  if (grade === 'B' || grade === 'B+') return '#2980b9';
  if (grade === 'C' || grade === 'C+') return WARNING_AMBER;
  return ERROR_RED;
}

function barColor(pct) {
  if (pct >= 80) return SUCCESS_GREEN;
  if (pct >= 60) return WARNING_AMBER;
  return ERROR_RED;
}

function SimpleSVGLineChart({ data, width = 400, height = 160 }) {
  if (!data || data.length < 2) return null;

  const scores = data.map((d) => d.score);
  const minVal = Math.min(...scores) - 5;
  const maxVal = Math.max(...scores) + 5;
  const range = maxVal - minVal || 1;

  const padX = 40;
  const padY = 20;
  const chartW = width - padX * 2;
  const chartH = height - padY * 2;

  const points = data.map((d, i) => ({
    x: padX + (i / (data.length - 1)) * chartW,
    y: padY + chartH - ((d.score - minVal) / range) * chartH,
  }));

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: 'block', margin: '0 auto' }}>
      <line x1={padX} y1={padY} x2={padX} y2={height - padY} stroke={BORDER_GRAY} strokeWidth={1} />
      <line x1={padX} y1={height - padY} x2={width - padX} y2={height - padY} stroke={BORDER_GRAY} strokeWidth={1} />

      {[0, 0.25, 0.5, 0.75, 1].map((t) => {
        const y = padY + chartH - t * chartH;
        const val = Math.round(minVal + t * range);
        return (
          <g key={t}>
            <line x1={padX} y1={y} x2={width - padX} y2={y} stroke={BG_LIGHT} strokeWidth={1} />
            <text x={padX - 6} y={y + 4} textAnchor="end" fontSize={11} fill={TEXT_MID}>{val}</text>
          </g>
        );
      })}

      <path d={pathD} fill="none" stroke={ACCENT} strokeWidth={2.5} strokeLinejoin="round" />

      {points.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r={4} fill={WHITE} stroke={ACCENT} strokeWidth={2} />
          <text x={p.x} y={height - 4} textAnchor="middle" fontSize={10} fill={TEXT_MID}>
            {data[i].label || `#${i + 1}`}
          </text>
        </g>
      ))}
    </svg>
  );
}

const ScoreReportRenderer = ({
  sceneManifest,
  rendererConfig = {},
  scene,
  feedback,
  children,
}) => {
  const [results, setResults] = useState(scene?.results || null);
  const [scoreHistory, setScoreHistory] = useState(scene?.score_history || []);
  const [loading, setLoading] = useState(!scene?.results);

  const sessionId = scene?.session_id || sceneManifest?.session_id;
  const domain = scene?.domain_id || sceneManifest?.domain_id;

  useEffect(() => {
    if (results || !sessionId) return;
    setLoading(true);
    Promise.all([
      testPrepAPI.getTestResults(sessionId).catch(() => null),
      domain ? testPrepAPI.getScoreHistory(domain).catch(() => null) : null,
    ]).then(([resResult, histResult]) => {
      if (resResult?.data) setResults(resResult.data);
      if (histResult?.data) setScoreHistory(histResult.data.scores || histResult.data.history || []);
      setLoading(false);
    });
  }, [sessionId, domain, results]);

  const r = results || {};
  const scaledScore = r.scaled_score ?? 0;
  const percentile = r.percentile ?? 0;
  const rawScore = r.raw_score ?? 0;
  const totalQuestions = r.total_questions ?? 0;
  const sections = r.sections || [];
  const categories = r.categories || [];
  const timeAnalysis = r.time_analysis || {};
  const timeGrade = r.time_management_grade || {};
  const strengths = r.strengths || [];
  const weaknesses = r.weaknesses || [];
  const recommendations = r.recommendations || [];

  if (loading) {
    return (
      <div style={{ ...styles.container, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: TEXT_MID }}>
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Preparing your score report...</div>
          <div style={{ fontSize: 14 }}>Analyzing performance data</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerTitle}>Score Report</div>
      </div>

      <div style={styles.hero}>
        <div style={styles.scoreCircle}>
          <span style={styles.scoreValue}>{scaledScore}</span>
          <span style={styles.scoreLabel}>out of 180</span>
        </div>
        <div style={styles.statBox}>
          <div style={styles.statValue}>{percentile}th</div>
          <div style={styles.statLabel}>Percentile</div>
        </div>
        <div style={styles.statBox}>
          <div style={styles.statValue}>{rawScore}/{totalQuestions}</div>
          <div style={styles.statLabel}>Raw Score</div>
        </div>
      </div>

      <div style={styles.content}>
        {scoreHistory.length >= 2 && (
          <div style={styles.card}>
            <div style={styles.cardTitle}>Score Trend</div>
            <SimpleSVGLineChart data={scoreHistory} width={600} height={180} />
          </div>
        )}

        {sections.length > 0 && (
          <div style={styles.card}>
            <div style={styles.cardTitle}>Section Breakdown</div>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Section</th>
                  <th style={styles.th}>Raw Score</th>
                  <th style={styles.th}>Accuracy</th>
                  <th style={styles.th}>Time Used</th>
                </tr>
              </thead>
              <tbody>
                {sections.map((s, i) => (
                  <tr key={i}>
                    <td style={styles.td}>{s.name || s.section_type_id || `Section ${i + 1}`}</td>
                    <td style={styles.td}>{s.correct}/{s.total}</td>
                    <td style={{ ...styles.td, color: barColor(s.accuracy || 0), fontWeight: 600 }}>
                      {(s.accuracy || 0).toFixed(0)}%
                    </td>
                    <td style={styles.td}>{s.time_used_display || `${Math.round((s.time_used_seconds || 0) / 60)}m`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {categories.length > 0 && (
          <div style={styles.card}>
            <div style={styles.cardTitle}>Accuracy by Category</div>
            <div style={styles.barContainer}>
              {categories.map((cat, i) => (
                <div key={i} style={styles.barRow}>
                  <span style={styles.barLabel}>{cat.name}</span>
                  <div style={styles.barTrack}>
                    <div
                      style={{
                        ...styles.barFill,
                        width: `${cat.accuracy || 0}%`,
                        background: barColor(cat.accuracy || 0),
                      }}
                    />
                  </div>
                  <span style={styles.barValue}>{(cat.accuracy || 0).toFixed(0)}%</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {(timeAnalysis.categories || []).length > 0 && (
          <div style={styles.card}>
            <div style={styles.cardTitle}>Time Analysis</div>
            <div style={styles.barContainer}>
              {timeAnalysis.categories.map((cat, i) => {
                const pctOfExpected = cat.expected_seconds
                  ? Math.round((cat.actual_seconds / cat.expected_seconds) * 100)
                  : 0;
                const overTime = pctOfExpected > 110;
                return (
                  <div key={i} style={styles.barRow}>
                    <span style={styles.barLabel}>{cat.name}</span>
                    <div style={styles.barTrack}>
                      <div
                        style={{
                          ...styles.barFill,
                          width: `${Math.min(pctOfExpected, 100)}%`,
                          background: overTime ? WARNING_AMBER : SUCCESS_GREEN,
                        }}
                      />
                    </div>
                    <span style={{ ...styles.barValue, color: overTime ? WARNING_AMBER : TEXT_MID }}>
                      {Math.round(cat.actual_seconds / 60)}m / {Math.round(cat.expected_seconds / 60)}m
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {timeGrade.grade && (
          <div style={styles.card}>
            <div style={styles.cardTitle}>Time Management</div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ ...styles.gradeBox, background: gradeColor(timeGrade.grade), color: WHITE }}>
                {timeGrade.grade}
              </div>
              <div style={styles.gradeExplanation}>
                {timeGrade.explanation || 'Your time management has been evaluated based on per-question pacing relative to section expectations.'}
              </div>
            </div>
          </div>
        )}

        {(strengths.length > 0 || weaknesses.length > 0) && (
          <div style={styles.card}>
            <div style={styles.cardTitle}>Performance Summary</div>
            <div style={styles.twoCol}>
              <div style={styles.col}>
                <div style={{ fontSize: 14, fontWeight: 700, color: SUCCESS_GREEN, marginBottom: 12 }}>
                  What Worked
                </div>
                {strengths.map((s, i) => (
                  <div key={i} style={styles.listItem}>
                    <span style={{ ...styles.bullet, color: SUCCESS_GREEN }}>✓</span>
                    <span>{s}</span>
                  </div>
                ))}
              </div>
              <div style={styles.col}>
                <div style={{ fontSize: 14, fontWeight: 700, color: ERROR_RED, marginBottom: 12 }}>
                  Areas for Improvement
                </div>
                {weaknesses.map((w, i) => (
                  <div key={i} style={styles.listItem}>
                    <span style={{ ...styles.bullet, color: ERROR_RED }}>✗</span>
                    <span>{w}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {recommendations.length > 0 && (
          <div style={styles.card}>
            <div style={styles.cardTitle}>Coach Recommendations</div>
            {recommendations.map((rec, i) => (
              <div key={i} style={styles.listItem}>
                <span style={{ ...styles.bullet, color: ACCENT }}>→</span>
                <span>{rec}</span>
              </div>
            ))}
          </div>
        )}

        <div style={styles.actions}>
          <button
            style={{ ...styles.actionBtn, background: ACCENT, color: WHITE }}
            onClick={() => scene?.onNavigate?.('test_review')}
          >
            Review Questions
          </button>
          <button
            style={{ ...styles.actionBtn, background: ACCENT_LIGHT, color: WHITE }}
            onClick={() => scene?.onNavigate?.('test_list')}
          >
            Take Another Test
          </button>
          <button
            style={{ ...styles.actionBtn, background: WHITE, color: ACCENT, border: `2px solid ${ACCENT}` }}
            onClick={() => scene?.onNavigate?.('score_history')}
          >
            View Score History
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScoreReportRenderer;
